/**
 * interior_textures.ts — procedural PBR texture sets for boardable interiors
 * (station bay/corridor/promenade + the Sovereign deck), 2026-08-24.
 *
 * No asset dependency: every map is drawn on a canvas at load and cached, so
 * floors/walls/ceilings get real surface detail (panel seams, rivets, grime,
 * scuffs) with matching normal / roughness / AO maps. Tiling (RepeatWrapping)
 * with per-surface repeat counts. Used by MeshStandardMaterial when the
 * interior is lit; the albedo alone still enriches a MeshBasicMaterial.
 *
 * Normal maps are derived from a drawn height field via a Sobel filter — the
 * seams and rivets catch the interior lights for a genuine bumped read.
 */
import * as THREE from 'three';

export type Surface = 'floor' | 'wall' | 'ceiling';
export interface PbrSet {
  map: THREE.CanvasTexture;
  normalMap: THREE.CanvasTexture;
  roughnessMap: THREE.CanvasTexture;
  aoMap: THREE.CanvasTexture;
}

const TILE = 512;
const cache = new Map<string, PbrSet>();

function mkCanvas(): [HTMLCanvasElement, CanvasRenderingContext2D] {
  const c = document.createElement('canvas');
  c.width = c.height = TILE;
  return [c, c.getContext('2d')!];
}

function tex(c: HTMLCanvasElement, srgb: boolean): THREE.CanvasTexture {
  const t = new THREE.CanvasTexture(c);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace;
  t.anisotropy = 8;
  return t;
}

/** Sobel a height canvas → a tangent-space normal canvas. */
function heightToNormal(height: HTMLCanvasElement, strength: number): HTMLCanvasElement {
  const g = height.getContext('2d')!;
  const src = g.getImageData(0, 0, TILE, TILE).data;
  const [out, og] = mkCanvas();
  const dst = og.createImageData(TILE, TILE);
  const at = (x: number, y: number): number => src[(((y + TILE) % TILE) * TILE + ((x + TILE) % TILE)) * 4] / 255;
  for (let y = 0; y < TILE; y++) {
    for (let x = 0; x < TILE; x++) {
      const dx = (at(x - 1, y) - at(x + 1, y)) * strength;
      const dy = (at(x, y - 1) - at(x, y + 1)) * strength;
      const len = Math.hypot(dx, dy, 1);
      const i = (y * TILE + x) * 4;
      dst.data[i] = ((dx / len) * 0.5 + 0.5) * 255;
      dst.data[i + 1] = ((dy / len) * 0.5 + 0.5) * 255;
      dst.data[i + 2] = ((1 / len) * 0.5 + 0.5) * 255;
      dst.data[i + 3] = 255;
    }
  }
  og.putImageData(dst, 0, 0);
  return out;
}

function rng(seed: number): () => number {
  let h = seed >>> 0;
  return () => { h ^= h << 13; h ^= h >>> 17; h ^= h << 5; return ((h >>> 0) / 4294967296); };
}

/** Draw the albedo + a matching height field for a surface preset. */
function drawSurface(surface: Surface, base: string, seam: string):
  { albedo: HTMLCanvasElement; height: HTMLCanvasElement } {
  const [albedo, ag] = mkCanvas();
  const [height, hg] = mkCanvas();
  const r = rng(surface === 'floor' ? 0x51 : surface === 'wall' ? 0x77 : 0x33);

  ag.fillStyle = base; ag.fillRect(0, 0, TILE, TILE);
  hg.fillStyle = '#808080'; hg.fillRect(0, 0, TILE, TILE); // mid height

  // Grime / mottle on the albedo (subtle large-scale variation).
  for (let i = 0; i < 900; i++) {
    const x = r() * TILE, y = r() * TILE, rad = 6 + r() * 60;
    ag.globalAlpha = 0.03 + r() * 0.05;
    ag.fillStyle = r() < 0.5 ? '#000000' : '#ffffff';
    ag.beginPath(); ag.arc(x, y, rad, 0, Math.PI * 2); ag.fill();
  }
  ag.globalAlpha = 1;

  const panel = (cols: number, rows: number, groove: number): void => {
    const cw = TILE / cols, ch = TILE / rows;
    ag.strokeStyle = seam; ag.lineWidth = groove;
    hg.strokeStyle = '#3a3a3a'; hg.lineWidth = groove;         // recessed seams (darker = lower)
    for (let i = 0; i <= cols; i++) { const x = i * cw; ag.beginPath(); ag.moveTo(x, 0); ag.lineTo(x, TILE); ag.stroke(); hg.beginPath(); hg.moveTo(x, 0); hg.lineTo(x, TILE); hg.stroke(); }
    for (let j = 0; j <= rows; j++) { const y = j * ch; ag.beginPath(); ag.moveTo(0, y); ag.lineTo(TILE, y); ag.stroke(); hg.beginPath(); hg.moveTo(0, y); hg.lineTo(TILE, y); hg.stroke(); }
    // Bevel highlight just inside each panel (raised edge).
    hg.strokeStyle = '#b8b8b8'; hg.lineWidth = Math.max(1, groove * 0.5);
    for (let i = 0; i < cols; i++) for (let j = 0; j < rows; j++) hg.strokeRect(i * cw + groove, j * ch + groove, cw - groove * 2, ch - groove * 2);
  };
  const rivets = (cols: number, rows: number): void => {
    const cw = TILE / cols, ch = TILE / rows;
    for (let i = 0; i < cols; i++) for (let j = 0; j < rows; j++) {
      for (const [ox, oy] of [[0.12, 0.12], [0.88, 0.12], [0.12, 0.88], [0.88, 0.88]]) {
        const x = (i + ox) * cw, y = (j + oy) * ch;
        ag.fillStyle = 'rgba(255,255,255,0.10)'; ag.beginPath(); ag.arc(x, y, 3, 0, Math.PI * 2); ag.fill();
        hg.fillStyle = '#e0e0e0'; hg.beginPath(); hg.arc(x, y, 3, 0, Math.PI * 2); hg.fill(); // raised bolt
      }
    }
  };

  if (surface === 'floor') {
    panel(4, 4, 5);
    rivets(4, 4);
    // Scuffs / scrapes.
    for (let i = 0; i < 40; i++) { ag.strokeStyle = 'rgba(0,0,0,0.10)'; ag.lineWidth = 1 + r() * 2; ag.beginPath(); const x = r() * TILE, y = r() * TILE; ag.moveTo(x, y); ag.lineTo(x + (r() - 0.5) * 80, y + (r() - 0.5) * 20); ag.stroke(); }
  } else if (surface === 'wall') {
    panel(3, 5, 4);
    rivets(3, 5);
  } else { // ceiling: ribbed
    ag.strokeStyle = seam; hg.strokeStyle = '#3a3a3a';
    ag.lineWidth = 6; hg.lineWidth = 6;
    for (let i = 0; i <= 10; i++) { const y = (i / 10) * TILE; ag.beginPath(); ag.moveTo(0, y); ag.lineTo(TILE, y); ag.stroke(); hg.beginPath(); hg.moveTo(0, y); hg.lineTo(TILE, y); hg.stroke(); }
    hg.strokeStyle = '#c0c0c0'; hg.lineWidth = 3;
    for (let i = 0; i < 10; i++) { const y = (i / 10) * TILE + TILE / 20; hg.beginPath(); hg.moveTo(0, y); hg.lineTo(TILE, y); hg.stroke(); }
  }
  return { albedo, height };
}

/** Roughness canvas: brighter (rougher) in seams/grime, smoother on panels. */
function roughnessFrom(height: HTMLCanvasElement, floor: number, span: number): HTMLCanvasElement {
  const hg = height.getContext('2d')!;
  const src = hg.getImageData(0, 0, TILE, TILE).data;
  const [out, og] = mkCanvas();
  const dst = og.createImageData(TILE, TILE);
  for (let i = 0; i < src.length; i += 4) {
    // Recessed seams (dark height) read rougher; raised bevels a touch glossier.
    const h = src[i] / 255;
    const rgh = Math.max(0, Math.min(1, floor + (1 - h) * span));
    dst.data[i] = dst.data[i + 1] = dst.data[i + 2] = rgh * 255; dst.data[i + 3] = 255;
  }
  og.putImageData(dst, 0, 0);
  return out;
}

/** AO canvas: darken the seams/corners a little for contact shadow. */
function aoFrom(height: HTMLCanvasElement): HTMLCanvasElement {
  const hg = height.getContext('2d')!;
  const src = hg.getImageData(0, 0, TILE, TILE).data;
  const [out, og] = mkCanvas();
  const dst = og.createImageData(TILE, TILE);
  for (let i = 0; i < src.length; i += 4) {
    const h = src[i] / 255;
    const ao = 0.55 + h * 0.45; // recessed = darker
    dst.data[i] = dst.data[i + 1] = dst.data[i + 2] = ao * 255; dst.data[i + 3] = 255;
  }
  og.putImageData(dst, 0, 0);
  return out;
}

const PRESET: Record<Surface, { base: string; seam: string; normalStrength: number; rough: [number, number] }> = {
  floor:   { base: '#2a3444', seam: '#141a26', normalStrength: 2.6, rough: [0.55, 0.35] },
  wall:    { base: '#38465e', seam: '#1c2534', normalStrength: 2.2, rough: [0.5, 0.35] },
  ceiling: { base: '#2f3a4c', seam: '#171f2b', normalStrength: 2.0, rough: [0.6, 0.3] },
};

/** Build (and cache) the PBR set for a surface. */
export function interiorPbr(surface: Surface): PbrSet {
  const hit = cache.get(surface);
  if (hit) return hit;
  const p = PRESET[surface];
  const { albedo, height } = drawSurface(surface, p.base, p.seam);
  const set: PbrSet = {
    map: tex(albedo, true),
    normalMap: tex(heightToNormal(height, p.normalStrength), false),
    roughnessMap: tex(roughnessFrom(height, p.rough[0], p.rough[1]), false),
    aoMap: tex(aoFrom(height), false),
  };
  cache.set(surface, set);
  return set;
}

// ── Baked-lit albedo (the interior scene is UNLIT — interior-materials law) ──
// The interior renders with MeshBasicMaterial (lit materials do not draw in the
// sector pipeline), so we bake the lighting response INTO one albedo map:
// panel albedo × directional shade from the height normal (a key light from
// upper-left) × ambient-occlusion in the seams. The result reads as
// normal-mapped, AO'd, faintly spec-sheened metal — permanently, no lights.
const bakedCache = new Map<Surface, THREE.CanvasTexture>();

export function interiorBakedMap(surface: Surface): THREE.CanvasTexture {
  const hit = bakedCache.get(surface);
  if (hit) return hit;
  const p = PRESET[surface];
  const { albedo, height } = drawSurface(surface, p.base, p.seam);
  const normal = heightToNormal(height, p.normalStrength);
  const ag = albedo.getContext('2d')!;
  const ng = normal.getContext('2d')!;
  const hg = height.getContext('2d')!;
  const alb = ag.getImageData(0, 0, TILE, TILE);
  const nrm = ng.getImageData(0, 0, TILE, TILE).data;
  const hgt = hg.getImageData(0, 0, TILE, TILE).data;
  // Key light from upper-left-front; a soft ambient floor so nothing goes black.
  const L = (() => { const v = [-0.5, -0.6, 0.6]; const l = Math.hypot(...v); return [v[0] / l, v[1] / l, v[2] / l]; })();
  const AMBIENT = 0.55, KEY = 0.7, SPEC = 0.35;
  const d = alb.data;
  for (let i = 0; i < d.length; i += 4) {
    const nx = (nrm[i] / 255) * 2 - 1, ny = (nrm[i + 1] / 255) * 2 - 1, nz = (nrm[i + 2] / 255) * 2 - 1;
    const ndotl = Math.max(0, nx * L[0] + ny * L[1] + nz * L[2]);
    const ao = 0.5 + (hgt[i] / 255) * 0.5;         // recessed seams darker
    const shade = (AMBIENT + KEY * ndotl) * ao;
    // Faux spec: a tight highlight where the surface faces the light.
    const spec = SPEC * Math.pow(ndotl, 12) * 255;
    d[i] = Math.min(255, d[i] * shade + spec);
    d[i + 1] = Math.min(255, d[i + 1] * shade + spec);
    d[i + 2] = Math.min(255, d[i + 2] * shade + spec);
  }
  ag.putImageData(alb, 0, 0);
  const t = tex(albedo, true);
  bakedCache.set(surface, t);
  return t;
}

/** A MeshBasicMaterial textured with the baked surface, tiled `repeat` times. */
export function interiorMaterial(surface: Surface, repeat: number): THREE.MeshBasicMaterial {
  const base = interiorBakedMap(surface);
  const t = base.clone();          // clone so each surface can set its own repeat
  t.needsUpdate = true;
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.repeat.set(repeat, repeat);
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 8;
  return new THREE.MeshBasicMaterial({ map: t });
}
